// assignSearch.ts — pure helpers for the post-call wrap-up "assign to deal /
// project" picker (call-assign-link-v1). Framework-agnostic (no React, no
// fetch) so the flatten / boost-sort / dedupe / keyboard-nav-order mechanics can
// be unit-tested in isolation (see assignSearch.test.ts).
//
// DECIDED DEFAULT (v1): the picker searches DEALS only. Projects are NOT matched
// directly — they appear nested under their parent deal. buildAssignRows takes
// the (already deal-filtered) deals plus a per-deal project map and flattens
// them into a single keyboard-navigable option list: each deal row is
// immediately followed by its project rows. The flattened order IS the
// arrow-key nav order; optionDomId is assign-opt-<n> over that flattened index.

export interface AssignRow {
  kind: 'deal' | 'project'
  /** Deal id (number) or project id (TEXT) — the value written as the owner. */
  id: number | string
  /** Primary chip / row text. */
  label: string
  /** Secondary muted row text (client, stage, project status, …). */
  sublabel: string
  /** For project rows, the id of the deal they nest under; null for deal rows. */
  parentDealId: number | string | null
  /** Stable DOM id / keyboard-nav anchor: assign-opt-<flattened index>. */
  optionDomId: string
}

export interface BuildAssignRowsOpts {
  /** Deal ids to boost to the top (e.g. the call's suggested/linked deals). */
  suggestedDealIds?: Array<number | string>
  /**
   * Prefix for each row's optionDomId (default 'assign-opt'). The picker threads
   * a per-instance useId() prefix here so two open pickers (the phone wrap-up +
   * a calls-list cell) never mint duplicate DOM ids / aria-activedescendant
   * targets (review finding: duplicate id="phone-assign-listbox" + optionDomIds).
   */
  idPrefix?: string
}

type ProjectMap =
  | Record<string | number, unknown[]>
  | Map<string | number, unknown[]>
  | null
  | undefined

function pick(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj?.[k]
    if (v !== null && v !== undefined && String(v).trim() !== '') return String(v)
  }
  return ''
}

function dealLabel(d: Record<string, unknown>): string {
  return pick(d, ['title', 'name', 'deal_name']) || `Deal ${d?.id}`
}

function dealSublabel(d: Record<string, unknown>): string {
  // Client / company ONLY. The stage is surfaced separately as the formatted
  // stage badge (phone-assign-opt-tag) — it must NEVER also appear here as a raw
  // underscored enum (e.g. "CONTRACT_SIGNED"), which duplicated the badge on the
  // calls-list picker rows (calls-assign dedupe). Never fall back to stage here.
  return pick(d, ['client_name', 'company_name', 'company'])
}

function projectLabel(p: Record<string, unknown>): string {
  return pick(p, ['name', 'title', 'project_name']) || `Project ${p?.id}`
}

function projectSublabel(p: Record<string, unknown>): string {
  return pick(p, ['status', 'stage', 'address']) || 'Project'
}

function projectsFor(map: ProjectMap, dealId: number | string): unknown[] {
  if (!map) return []
  if (map instanceof Map) {
    return (map.get(dealId) as unknown[]) || (map.get(String(dealId)) as unknown[]) || []
  }
  const rec = map as Record<string | number, unknown[]>
  return rec[dealId as string | number] || rec[String(dealId)] || []
}

/**
 * Flatten deals + their nested projects into a single keyboard-navigable option
 * list. Deals are deduped by id (first occurrence wins) and stable-boost-sorted
 * so suggestedDealIds come first while each group keeps its original relative
 * order. Each deal row is immediately followed by its project rows; optionDomId
 * is assign-opt-<n> over the final flattened order (the arrow-key nav order).
 */
export function buildAssignRows(
  deals: Array<Record<string, unknown>> | null | undefined,
  projectsByDealId: ProjectMap,
  opts: BuildAssignRowsOpts = {}
): AssignRow[] {
  const suggested = new Set((opts?.suggestedDealIds || []).map((x) => String(x)))
  const idPrefix = opts?.idPrefix || 'assign-opt'

  // Dedupe deals by id, preserving first occurrence.
  const seen = new Set<string>()
  const uniqueDeals: Array<Record<string, unknown>> = []
  for (const d of deals || []) {
    if (!d || d.id === null || d.id === undefined) continue
    const key = String(d.id)
    if (seen.has(key)) continue
    seen.add(key)
    uniqueDeals.push(d)
  }

  // Stable boost-sort: suggested deals first, original relative order within
  // each group preserved (a stable partition, not a re-order by suggested order).
  const boosted = [
    ...uniqueDeals.filter((d) => suggested.has(String(d.id))),
    ...uniqueDeals.filter((d) => !suggested.has(String(d.id))),
  ]

  const rows: AssignRow[] = []
  let n = 0
  for (const d of boosted) {
    const dealId = d.id as number | string
    rows.push({
      kind: 'deal',
      id: dealId,
      label: dealLabel(d),
      sublabel: dealSublabel(d),
      parentDealId: null,
      optionDomId: `${idPrefix}-${n++}`,
    })
    for (const p of projectsFor(projectsByDealId, dealId) as Array<Record<string, unknown>>) {
      if (!p || p.id === null || p.id === undefined) continue
      rows.push({
        kind: 'project',
        id: p.id as number | string,
        label: projectLabel(p),
        sublabel: projectSublabel(p),
        parentDealId: dealId,
        optionDomId: `${idPrefix}-${n++}`,
      })
    }
  }
  return rows
}

/**
 * The deal ids whose nested projects the wrap-up picker must lazily probe.
 * Returns EVERY deal id (deduped, skipping null/undefined ids) UNCONDITIONALLY
 * — deliberately NOT gated on `project_count`.
 *
 * REVIEW FINDING #2 (call-assign-link-v1 cross-contract drift): the serving
 * endpoints that feed the picker rows (fetchDeals search + fetchClientDeals /
 * GET /api/crm/clients/:id/deals) do NOT stamp `project_count`, so a
 * `project_count > 0` gate silently hid nested projects under a searched deal
 * and broke the golden path. Probing GET /api/crm/deals/:id/projects is a
 * cheap indexed query; over-probing a childless deal just caches an empty
 * list. Route the picker's probe set through here so the count gate can never
 * be reintroduced without failing this helper's tests.
 */
export function dealsToProbe(
  deals: Array<Record<string, unknown>> | null | undefined,
): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const d of deals || []) {
    if (!d || d.id === null || d.id === undefined) continue
    const key = String(d.id)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}

/**
 * The minimum reachable active index for the picker's keyboard model. The
 * "Clear assignment" affordance lives at index -1 (BEFORE row 0) and is present
 * ONLY when the call already has an owner (`hasClear`). When it's absent the
 * model must never reach -1 — otherwise aria-activedescendant would point at a
 * clear row that isn't rendered (a dead id). Anchored picker renders the clear
 * row conditionally, so this gate keeps the keyboard model honest.
 */
export function assignMinActiveIndex(hasClear: boolean): number {
  return hasClear ? -1 : 0
}

/**
 * Advance / clamp the active row index for ArrowDown ('down') or ArrowUp ('up')
 * given the flattened row count. Floors at assignMinActiveIndex(hasClear) and
 * ceilings at the last row (rowCount - 1). Pure — the picker's onKeyDown routes
 * through here so the "no index -1 when the clear row is hidden" invariant is
 * unit-testable (see assignSearch.test.ts).
 */
export function nextAssignActiveIndex(
  current: number,
  direction: 'up' | 'down',
  rowCount: number,
  hasClear: boolean,
): number {
  const min = assignMinActiveIndex(hasClear)
  if (direction === 'down') return Math.max(min, Math.min(current + 1, rowCount - 1))
  return Math.max(min, current - 1)
}

/**
 * Render the concise chip text for a staged assignment selection. Returns ''
 * for an empty selection so callers can treat "no chip" uniformly.
 */
export function formatAssignChip(selection: AssignRow | null | undefined): string {
  if (!selection) return ''
  const label = selection.label || (selection.kind === 'project' ? `Project ${selection.id}` : `Deal ${selection.id}`)
  return selection.kind === 'project' ? `${label} (project)` : label
}
